/**
 * Fetch wrapper. Sends the access token, refreshes once on 401, and
 * returns `{ error, code, status }` instead of throwing on HTTP failures.
 */

const API_ROOT = '/api/v1';

let refreshing = null;

function token() {
  try {
    return localStorage.getItem('accessToken') || '';
  } catch {
    return '';
  }
}

function clearSession() {
  try {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  } catch {
    /* ignore */
  }
}

async function refreshAccessToken() {
  let refreshToken = '';
  try {
    refreshToken = localStorage.getItem('refreshToken') || '';
  } catch {
    return null;
  }
  if (!refreshToken) return null;
  const resp = await fetch(`${API_ROOT}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  if (!resp.ok) return null;
  const data = await resp.json().catch(() => null);
  if (!data || !data.accessToken) return null;
  localStorage.setItem('accessToken', data.accessToken);
  if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
  return data.accessToken;
}

function refreshOnce() {
  if (!refreshing) {
    refreshing = refreshAccessToken().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

function failure(status, body) {
  const payload = body && typeof body === 'object' ? body : {};
  const out = {
    error: payload.error || payload.message || 'Request failed',
    code: payload.code || (status === 401 ? 'auth' : 'http'),
    status,
  };
  // Sync replies use 409 with cooldown / running and no error string.
  if (payload.cooldown) out.cooldown = true;
  if (payload.running) out.running = true;
  if (payload.message) out.message = payload.message;
  if (payload.ok === false) out.ok = false;
  // Analysis refusals point at the run already active in the chat.
  if (payload.run_id) out.run_id = String(payload.run_id);
  return out;
}

/**
 * @param {string} path Path under `/api/v1`, starting with `/`.
 * @param {{ method?: string, body?: unknown, headers?: Record<string, string>, retried?: boolean }} [options]
 */
export async function api(path, options = {}) {
  const headers = {
    Accept: 'application/json',
    ...(options.body != null ? { 'Content-Type': 'application/json' } : {}),
    ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
    ...(options.headers || {}),
  };
  let body;
  if (options.body != null) {
    body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body);
  }
  let resp;
  try {
    resp = await fetch(`${API_ROOT}${path}`, { method: options.method || 'GET', headers, body });
  } catch {
    return { error: 'Connection failed', code: 'network', status: 0 };
  }
  if (resp.status === 401 && !options.retried) {
    const next = await refreshOnce();
    if (next) return api(path, { ...options, retried: true });
    clearSession();
    return failure(401, { error: 'Sign in again', code: 'auth' });
  }
  if (resp.status === 204) return null;
  const raw = await resp.text();
  let parsed = null;
  if (raw) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return failure(resp.status, { error: 'Bad response', code: 'http' });
    }
  }
  if (!resp.ok) return failure(resp.status, parsed);
  return parsed;
}
